import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Input, useToast, v } from '@kss/ui';
import { CreditCard, Plus, Smartphone, Trash2, Wallet } from 'lucide-react';
import { useAuth } from '@kss/auth';

/**
 * Saved payment methods.
 *
 * Scope today: UPI VPAs (public identifiers, safe to store locally)
 * + the always-on COD entry. Card storage is intentionally NOT
 * implemented client-side — storing PANs in the browser would
 * violate PCI-DSS even if it were just `last4`. When the backend
 * adds a tokenisation flow (Razorpay / Stripe / similar) we wire
 * the "Add card" path then, with the gateway returning a token we
 * persist server-side. See BACKEND_AGENT_PROMPTS Item 5.
 *
 * Persistence: per-user `localStorage` at `kss.payments.v1:<userId>`,
 * mirroring the cart / address-book / wishlist storage shape.
 */

interface UpiMethod {
  id: string;
  kind: 'upi';
  vpa: string;
  isDefault?: boolean;
}

const STORAGE_PREFIX = 'kss.payments.v1';

function storageKey(userId?: string | null): string {
  return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

function readMethods(userId?: string | null): UpiMethod[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) return JSON.parse(raw) as UpiMethod[];
  } catch {
    /* malformed JSON — start clean */
  }
  return [];
}

export default function AccountPaymentMethods() {
  const toast = useToast();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [items, setItems] = useState<UpiMethod[]>(() => readMethods(userId));
  const [adding, setAdding] = useState(false);
  const [vpa, setVpa] = useState('');
  const [vpaTouched, setVpaTouched] = useState(false);
  /**
   * UPI VPA rules.
   *
   * The format check and the duplicate check both existed already, but
   * only as toasts fired on submit — nothing appeared under the field
   * and the button stayed enabled, so the user had to guess which part
   * was wrong. Same rules, now surfaced field-level and used to gate
   * the button. `notDuplicate` also makes the comparison
   * case-insensitive: `Name@bank` and `name@bank` are the same VPA.
   */
  const vpaError = v.runRules(vpa, [
    v.required('UPI ID'),
    v.maxLength(64, 'UPI ID'),
    v.allowedChars(/^[\w.\-]+@[\w]+$/, 'must look like name@bank', 'UPI ID'),
    v.notDuplicate(items.map((m) => m.vpa), 'This UPI ID'),
  ]);
  const lastUserId = useRef<string | null>(userId);

  // Re-load on user switch — same per-user storage discipline as the
  // cart, addresses, wishlist.
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    setItems(readMethods(userId));
  }, [userId]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(items));
    } catch {
      /* storage full / disabled — silent */
    }
  }, [items, userId]);

  function addUpi(e: React.FormEvent) {
    e.preventDefault();
    setVpaTouched(true);
    if (vpaError) {
      toast.error('Invalid UPI ID', vpaError);
      return;
    }
    const trimmed = vpa.trim();
    const id = 'pm_' + Math.random().toString(36).slice(2, 8);
    // First UPI auto-promotes to default so checkout has a sane
    // pick to pre-select; subsequent additions stay non-default.
    const isDefault = items.length === 0;
    setItems((p) => [...p, { id, kind: 'upi', vpa: trimmed, isDefault }]);
    toast.success('UPI saved', trimmed);
    setVpa('');
    setAdding(false);
  }

  function makeDefault(id: string) {
    setItems((p) => p.map((m) => ({ ...m, isDefault: m.id === id })));
  }

  function removeOne(id: string) {
    setItems((p) => {
      const removed = p.find((m) => m.id === id);
      const next = p.filter((m) => m.id !== id);
      if (removed?.isDefault && next.length > 0) {
        next[0] = { ...next[0]!, isDefault: true };
      }
      return next;
    });
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
          Payment methods
        </h1>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add UPI
          </Button>
        )}
      </div>

      <Alert variant="info">
        <strong>Card storage coming soon.</strong>{' '}
        Saving cards requires a tokenisation provider — until that
        ships, only UPI IDs and Cash on Delivery are stored on your
        account. You can still pay by card at checkout via the
        secure gateway.
      </Alert>

      {items.map((m) => (
        <Card key={m.id} className="flex items-start justify-between gap-3 p-4">
          <div className="flex items-start gap-3">
            <Smartphone className="mt-0.5 h-5 w-5 text-[var(--color-primary)]" />
            <div>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                UPI · {m.vpa}
                {m.isDefault && (
                  <span className="ml-2 rounded bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[var(--color-primary)]">
                    Default
                  </span>
                )}
              </p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                Pay via any UPI app.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!m.isDefault && (
              <Button size="sm" variant="outline" onClick={() => makeDefault(m.id)}>
                Make default
              </Button>
            )}
            <button
              type="button"
              onClick={() => removeOne(m.id)}
              aria-label="Remove method"
              className="inline-flex h-8 w-8 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </Card>
      ))}

      {/* Cash on delivery tile — always rendered, can't be removed
          (it's a checkout option, not a saved instrument). */}
      <Card className="flex items-start gap-3 p-4">
        <Wallet className="mt-0.5 h-5 w-5 text-[var(--color-primary)]" />
        <div>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            Cash on delivery
          </p>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Pay in cash when the order arrives.
          </p>
        </div>
      </Card>

      {/* Card placeholder tile — disabled until a tokenisation
          provider is wired. Keeping it visible here (rather than
          hidden) so customers know card payments aren't gone, just
          handled at checkout. */}
      <Card className="flex items-start gap-3 p-4 opacity-60">
        <CreditCard className="mt-0.5 h-5 w-5 text-[var(--color-text-muted)]" />
        <div>
          <p className="text-sm font-semibold text-[var(--color-text-primary)]">
            Card (at checkout)
          </p>
          <p className="text-xs text-[var(--color-text-secondary)]">
            Use any Visa, Mastercard, RuPay or AmEx card during checkout.
            Saved-card support is on the roadmap.
          </p>
        </div>
      </Card>

      {adding && (
        <Card className="p-5">
          <form onSubmit={addUpi} className="grid gap-3">
            <Input
              label="UPI ID"
              value={vpa}
              onChange={(e) => setVpa(e.target.value)}
              onBlur={() => setVpaTouched(true)}
              error={vpaTouched ? vpaError ?? undefined : undefined}
              placeholder="name@bank"
              autoComplete="off"
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setVpa('');
                  setAdding(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!!vpaError}>Save UPI</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
